import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Pagination, SwipeableDrawer, useTheme } from '@mui/material';
import {
  Banner,
  Button,
  DatePicker,
  Dropdown,
  Flex,
  IconButton,
  Loader,
  SlidingPanel,
  Text,
  TextToolTip,
} from '@components';
import { useUserTransactionsQuery } from '@features/api/userPortfolioSlice';
import {
  TransactionStatus,
  UserTransaction,
} from '@features/api/response/UserTransactions';
import { FormattedMessage, useIntl } from 'react-intl';
import { Config } from '@config/config';
import { useState, useEffect } from 'react';
import RestrictedProductTab from '@components/RestrictedProductTab/RestrictedProductTab';
import Background from '@assets/backgrounds/portfolio/desktop/portfolio-transactions-overall-mask.png';
import BackgroundMobile from '@assets/backgrounds/portfolio/mobile/portfolio-transactions-overall-mask.png';
import { useNavigate } from 'react-router-dom';
import { Lock } from '@assets/svg';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { useUserQuery } from '@features/api/userSlice';
import { useProductsQuery } from '@features/api/productsSlice';
import {
  amountRangeMenuItems,
  getStatusProps,
  headerCells,
} from './Transactions.consts';
import { useTransactionsFilter } from './Transaction.hooks';
import {
  BackButton,
  BottomPanelButtonsContainer,
  TableContainer,
} from './Transactions.styles';
import { CancelIcon } from '@pages/WrapperPages/Menu/Menu.styles';
import { InvestorQualificationStage } from '@features/api/response/User';
import { useLazyGetInvestmentIntentQuery } from '@features/api/investmentSlice';
import { EnhancedTableHead, Order } from '@components/Table/EnhancedTableHead';
import { getComparator } from '@utils/table/tableUtils';
import { noop } from 'lodash';
import PaymentInstructions from './PaymentInstructions';
import { SortFilterProperties } from './Transactions.types';

const rowsPerPage = 8;
export default function Transactions() {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { sm: isMobile } = useMediaQueries();

  const { data: initialTransactions, isLoading: isLoadingTransactions } =
    useUserTransactionsQuery(undefined, { refetchOnMountOrArgChange: true });

  const { data: user, isLoading: isLoadingUser } = useUserQuery();
  const [getInvestmentIntentQuery, { data: investmentIntent }] =
    useLazyGetInvestmentIntentQuery();
  const isProfileNotCompleted =
    user?.investorQualificationStage ===
    InvestorQualificationStage.AwaitingQuestionnaire;
  const isUnderReview =
    user?.investorQualificationStage ===
    InvestorQualificationStage.QuestionnaireSubmitted;

  const { data: products, isLoading: isLoadingProducts } = useProductsQuery(
    undefined,
    {
      skip: !user,
    }
  );

  const activeProductId = products?.filter((x) => x.active)?.[0].id;

  const {
    values,
    handlers: {
      handleProductFilterChange,
      handleTypeFilterChange,
      handleDateToFilterChange,
      handleDateFromFilterChange,
      handleAmountRangeFilterChange,
      handleSearchButtonClick,
      handleClearButtonClick,
    },
  } = useTransactionsFilter(initialTransactions ?? []);
  const {
    availableProducts,
    availableTypes,
    filterCriteria,
    filteredData,
    isSearchButtonEnabled,
  } = values;

  const filtersLength = Object.values(filterCriteria ?? {}).length;

  const underReviewAndApprovedLength =
    initialTransactions?.filter(
      (x) =>
        x.status === TransactionStatus.Approved ||
        x.status === TransactionStatus.InReview
    ).length ?? 0;

  const hasTransactionUnderReview =
    underReviewAndApprovedLength === 1 &&
    initialTransactions?.find((x) => x.status === TransactionStatus.InReview);
  const hasTransactionApproved =
    underReviewAndApprovedLength === 1 &&
    initialTransactions?.find((x) => x.status === TransactionStatus.Approved);
  const hasAwaitingPaymentTransaction = initialTransactions?.find(
    (x) => x.status === TransactionStatus.AwaitingPayment
  );

  const [paymentInstructionsTransaction, setPaymentInstructionsTransaction] =
    useState<UserTransaction>();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] =
    useState<keyof SortFilterProperties>('investmentDate');
  const [page, setPage] = useState(1);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  useEffect(() => {
    if (
      activeProductId &&
      user?.investorQualificationStage ===
        InvestorQualificationStage.CompletedQualified &&
      !initialTransactions?.length
    ) {
      getInvestmentIntentQuery({
        productId: activeProductId,
      });
    }
  }, [user, initialTransactions, getInvestmentIntentQuery, activeProductId]);

  const handleRequestSort = (
    _e: unknown,
    property: keyof SortFilterProperties
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleInvestButtonClick = (): void => {
    if (user?.personalDetailsLocked || investmentIntent?.id) {
      navigate(`/investment-subscription/${activeProductId}/amount`, {
        state: {
          progress: 0,
        },
      });
    } else {
      navigate(`/investment-get-started/${activeProductId}/subscription-intro`);
    }
  };

  const handleClosePaymentInstructionsDialog = () => {
    setPaymentInstructionsTransaction(undefined);
  };

  const renderFilters = () => (
    <>
      <Box flex={1}>
        <Dropdown
          label={intl.formatMessage({
            id: 'portfolio.transactions.product',
          })}
          menuItemData={availableProducts}
          onChange={handleProductFilterChange}
          value={filterCriteria?.productName ?? ''}
          defaultValue={filterCriteria?.productName ?? ''}
          menuAutoWidth
        />
      </Box>
      <Box flex={1}>
        <Dropdown
          label={intl.formatMessage({
            id: 'portfolio.transactions.transactionType',
          })}
          menuItemData={availableTypes}
          onChange={handleTypeFilterChange}
          value={filterCriteria?.type ?? ''}
          defaultValue={filterCriteria?.type ?? ''}
          menuAutoWidth
        />
      </Box>
      <DatePicker
        label={intl.formatMessage({
          id: 'portfolio.transactions.dateFrom',
        })}
        mobileActions={['accept']}
        disableFuture
        showToolbar={false}
        value={filterCriteria?.subscribedDateFrom ?? null}
        inputFormat='DD/MM/YYYY'
        maxDate={filterCriteria?.subscribedDateTo}
        onChange={handleDateFromFilterChange}
      />
      <DatePicker
        label={intl.formatMessage({ id: 'portfolio.transactions.dateTo' })}
        mobileActions={['accept']}
        disableFuture
        showToolbar={false}
        value={filterCriteria?.subscribedDateTo ?? null}
        inputFormat='DD/MM/YYYY'
        minDate={filterCriteria?.subscribedDateFrom}
        onChange={handleDateToFilterChange}
      />

      <Box flex={1}>
        <Dropdown
          label={intl.formatMessage({
            id: 'portfolio.transactions.amountRange',
          })}
          menuItemData={amountRangeMenuItems}
          value={filterCriteria?.amountRange ?? ''}
          onChange={handleAmountRangeFilterChange}
          menuAutoWidth
        />
      </Box>
    </>
  );

  if (!filteredData) return <></>;
  if (isProfileNotCompleted || isUnderReview) {
    return (
      <Flex flex='1 0 100%'>
        <RestrictedProductTab
          backgroundImageUrl={!isMobile ? Background : BackgroundMobile}
          text={intl.formatMessage({
            id: !isUnderReview
              ? 'portfolio.completeQuestionnaireAndInvestToUnlock'
              : 'home.profileUnderReview',
          })}
          button={
            !isUnderReview
              ? {
                  label: intl.formatMessage({
                    id: 'misc.completeQuestionnaire',
                  }),
                  onClick: () => navigate('/questionnaire-intro'),
                  iconLeft: <Lock />,
                }
              : undefined
          }
        />
      </Flex>
    );
  } else if (!initialTransactions?.length && !isLoadingTransactions) {
    return (
      <Flex flex='1 0 100%'>
        <RestrictedProductTab
          backgroundImageUrl={!isMobile ? Background : BackgroundMobile}
          text={intl.formatMessage({
            id: 'portfolio.investToUnlock',
          })}
          button={{
            label: intl.formatMessage({
              id: 'product.invest',
            }),
            onClick: handleInvestButtonClick,
          }}
        />
      </Flex>
    );
  }

  const totalPages = Math.ceil(filteredData?.length / rowsPerPage);
  return (
    <Flex
      flex={1}
      width='100%'
      mt={hasTransactionUnderReview || hasTransactionApproved ? 14 : 19}
      flexDirection='column'
      data-testid='transactions-container'
    >
      <Loader
        isLoading={isLoadingProducts || isLoadingTransactions || isLoadingUser}
      />
      {hasTransactionUnderReview ? (
        <Flex width={isMobile ? '100%' : '60%'} marginBottom={12}>
          <Banner color='warning' icon={InfoIcon}>
            <Text
              variant='h6'
              fontWeight='fontWeightMedium'
              color={theme.palette.primary.main}
            >
              <FormattedMessage id='portfolio.transactions.reviewDisclaimer' />
            </Text>
          </Banner>
        </Flex>
      ) : null}
      {hasTransactionApproved ? (
        <Flex width={isMobile ? '100%' : '60%'} marginBottom={12}>
          <Banner color='warning' icon={InfoIcon}>
            <Text
              variant='h6'
              fontWeight='fontWeightMedium'
              color={theme.palette.primary.main}
            >
              <FormattedMessage id='portfolio.transactions.approvedDisclaimer' />
            </Text>
          </Banner>
        </Flex>
      ) : null}

      {!isMobile ? (
        <>
          <Flex>
            <BackButton variant='tertiary' onClick={handleClearButtonClick}>
              <FormattedMessage id='misc.clearAll' />
            </BackButton>
          </Flex>
          <Flex gap={6} alignItems='center' marginBottom={10}>
            {renderFilters()}
            <Flex>
              <Button
                variant='primary'
                sx={{ height: 50 }}
                disabled={!filtersLength || !isSearchButtonEnabled}
                onClick={handleSearchButtonClick}
              >
                <FormattedMessage id='misc.search' />
              </Button>
            </Flex>
          </Flex>
        </>
      ) : (
        <Flex width='100%' marginY={10}>
          <Button
            variant='secondary'
            onClick={() => setIsBottomSheetOpen(true)}
            fullWidth
            sx={{ height: 45 }}
          >
            {`${intl.formatMessage({ id: 'portfolio.transactions.filter' })} ${
              filtersLength ? `(${filtersLength})` : ''
            }`}
          </Button>
        </Flex>
      )}
      <TableContainer>
        <Flex overflow='auto'>
          <Table aria-labelledby='tableTitle'>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headerCells={headerCells}
            />
            <TableBody>
              {filteredData.length ? (
                filteredData
                  ?.slice()
                  .sort((a, b) => {
                    const result = getComparator<keyof SortFilterProperties>(
                      order,
                      orderBy
                    )(a, b);
                    const equalValuesResult = 0;
                    // If the values are equal we additionally sort on the ID
                    if (result === equalValuesResult) {
                      const idComparisonResult = getComparator(order, 'id')(
                        a,
                        b
                      );
                      return idComparisonResult;
                    }

                    return result;
                  })
                  .slice(
                    (page - 1) * rowsPerPage,
                    (page - 1) * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${row.id}`;
                    const statusData = getStatusProps(row.status, theme);
                    const isAwaitingPayment =
                      row.status === TransactionStatus.AwaitingPayment;

                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell
                          component='th'
                          id={labelId}
                          scope='row'
                          padding='normal'
                        >
                          {row.productName}
                        </TableCell>
                        <TableCell align='left'>{row.type}</TableCell>
                        <TableCell align='left'>
                          {row.status !== TransactionStatus.Invested
                            ? '-'
                            : row.subscriptionDate}
                        </TableCell>
                        <TableCell align='left'>
                          {row.status !== TransactionStatus.Invested
                            ? '-'
                            : row.investmentDate}
                        </TableCell>
                        <TableCell
                          align='left'
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textDecoration: isAwaitingPayment
                              ? 'underline'
                              : 'none',
                            cursor: isAwaitingPayment ? 'pointer' : 'auto',
                          }}
                          onClick={() =>
                            isAwaitingPayment
                              ? setPaymentInstructionsTransaction(row)
                              : noop
                          }
                        >
                          {statusData.tooltip ? (
                            <TextToolTip
                              text={statusData.label}
                              textProps={{
                                variant: 'bodyBig',
                                fontWeight: 'fontWeightSemiBold',
                                sx: {
                                  color: statusData.color,
                                  textDecoration: 'underline',
                                },
                              }}
                              toolTipText={statusData.tooltip}
                              icon={() => (
                                <InfoOutlinedIcon
                                  fontSize='small'
                                  sx={{ color: statusData.color }}
                                />
                              )}
                            />
                          ) : (
                            <Text
                              variant='bodyBig'
                              fontWeight='fontWeightSemiBold'
                              sx={{
                                color: statusData.color,
                                textDecoration: 'underline',
                              }}
                            >
                              {statusData.label}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          {intl.formatNumber(row.amount, {
                            style: 'currency',
                            currency: row.currency ?? Config.currency.code,
                            maximumFractionDigits: 2,
                            notation: 'standard',
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Text
                      variant='h5'
                      fontWeight='fontWeightMedium'
                      sx={{ color: theme.palette.grey[700] }}
                    >
                      <FormattedMessage id='misc.noResults' />
                    </Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Flex>
        <Flex width='100%' justifyContent='center' padding={8}>
          <Pagination
            color='primary'
            shape='rounded'
            count={totalPages}
            page={page}
            onChange={handleChangePage}
          />
        </Flex>
      </TableContainer>
      {isMobile ? (
        <SwipeableDrawer
          anchor='bottom'
          open={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          onOpen={() => setIsBottomSheetOpen(true)}
          keepMounted={false}
          PaperProps={{
            sx: {
              height: '90vh',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
            },
          }}
        >
          <Box padding={10}>
            <Flex flex={1} alignItems='center' justifyContent='space-between'>
              <Text variant='h4' fontWeight='fontWeightMedium'>
                <FormattedMessage id='portfolio.transactions.selectFilters' />
              </Text>
              <IconButton
                data-testid='close-button'
                size='small'
                iconBoxHeight={25}
                iconBoxWidth={25}
                icon={() => <CancelIcon color='info' />}
                onClick={() => setIsBottomSheetOpen(false)}
              />
            </Flex>
            <Flex marginTop={18} gap={10} flexDirection='column'>
              {renderFilters()}
            </Flex>
            <BottomPanelButtonsContainer>
              <Button
                variant='secondary'
                sx={{ flex: 1, height: 45 }}
                onClick={handleClearButtonClick}
              >
                <FormattedMessage id='misc.clear' />
              </Button>
              <Button
                sx={{ flex: 1, height: 45 }}
                disabled={!filtersLength || !isSearchButtonEnabled}
                onClick={() => {
                  handleSearchButtonClick();
                  setIsBottomSheetOpen(false);
                }}
              >
                {`${intl.formatMessage({
                  id: 'misc.apply',
                })} ${filtersLength ? `(${filtersLength})` : ''}`}
              </Button>
            </BottomPanelButtonsContainer>
          </Box>
        </SwipeableDrawer>
      ) : null}
      {hasAwaitingPaymentTransaction ? (
        <SlidingPanel
          PaperProps={{ sx: { width: { xs: '100%', md: '35%' } } }}
          anchor='right'
          title={intl.formatMessage({
            id: 'portfolio.transactions.paymentInstructions.paymentInstructions',
          })}
          open={!!paymentInstructionsTransaction}
          onClose={handleClosePaymentInstructionsDialog}
        >
          <Box mt={10}>
            {paymentInstructionsTransaction && (
              <PaymentInstructions
                transaction={paymentInstructionsTransaction}
                user={user}
              />
            )}
          </Box>
        </SlidingPanel>
      ) : null}
    </Flex>
  );
}
